import {assign, setup} from 'xstate';

import {updateFieldHelper, validateFieldHelper} from '@/commons/machines';

import {MULTI_CHOICE_FIELDS} from '../constants';
import type {FhsContext, FhsEvent, FhsStepName} from '../types';
import {computeNextValues} from '../utils';

import {getSchemaForStep} from './schemas';

const initialContext: FhsContext = {
  formData: {},
  errors: {},
  touched: {},
};

export const fhsMachine = setup({
  types: {context: {} as FhsContext, events: {} as FhsEvent},
  guards: {
    // Parameterized so every step shares one guard; transitions pass their step.
    canContinue: ({context}, params: {step: FhsStepName}) => {
      const schema = getSchemaForStep(params.step);

      return schema ? schema.safeParse(context.formData).success : true;
    },
  },
  actions: {
    assignField: assign(({context, event}) =>
      updateFieldHelper(context, event),
    ),
    markTouched: assign(({context, event}) => {
      if (event.type !== 'UPDATE_FIELD') return {};

      return {touched: {...context.touched, [event.field]: true}};
    }),
    // Multi-choice toggle with exclusive-option awareness. Field-to-exclusive
    // mapping lives in constants.MULTI_CHOICE_FIELDS so components stay
    // declarative; the toggle logic itself is in computeNextValues.
    toggleMultiChoice: assign(({context, event}) => {
      if (event.type !== 'TOGGLE_MULTI_CHOICE') return {};

      const {field, value} = event;
      const exclusive = MULTI_CHOICE_FIELDS[field]?.exclusiveValue;
      const raw = context.formData[field];
      const selectedValues = Array.isArray(raw) ? raw : [];

      const nextValues = computeNextValues(selectedValues, value, exclusive);

      return {
        formData: {...context.formData, [field]: nextValues},
        touched: {...context.touched, [field]: true},
      };
    }),
    validateStep: assign(({context}, params: {step: FhsStepName}) => {
      const schema = getSchemaForStep(params.step);

      if (!schema) return {};

      const {errors = {}} = validateFieldHelper(context.formData, schema);

      return {errors};
    }),
  },
}).createMachine({
  id: 'fhs',
  initial: 'name',
  context: initialContext,
  states: {
    name: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'name'}},
          ],
        },
        NEXT: {
          target: 'age',
          guard: {type: 'canContinue', params: {step: 'name'}},
        },
      },
    },
    age: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'age'}},
          ],
        },
        NEXT: {
          target: 'maritalStatus',
          guard: {type: 'canContinue', params: {step: 'age'}},
        },
        BACK: {target: 'name'},
      },
    },
    maritalStatus: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'maritalStatus'}},
          ],
        },
        NEXT: {
          target: 'financialDependents',
          guard: {type: 'canContinue', params: {step: 'maritalStatus'}},
        },
        BACK: {target: 'age'},
      },
    },
    financialDependents: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'financialDependents'}},
          ],
        },
        NEXT: {
          target: 'retirement',
          guard: {type: 'canContinue', params: {step: 'financialDependents'}},
        },
        BACK: {target: 'maritalStatus'},
      },
    },
    retirement: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'retirement'}},
          ],
        },
        NEXT: {
          target: 'insurance',
          guard: {type: 'canContinue', params: {step: 'retirement'}},
        },
        BACK: {target: 'financialDependents'},
      },
    },
    insurance: {
      on: {
        TOGGLE_MULTI_CHOICE: {
          actions: [
            'toggleMultiChoice',
            {type: 'validateStep', params: {step: 'insurance'}},
          ],
        },
        NEXT: {
          target: 'estatePlanning',
          guard: {type: 'canContinue', params: {step: 'insurance'}},
        },
        BACK: {target: 'retirement'},
      },
    },
    estatePlanning: {
      on: {
        TOGGLE_MULTI_CHOICE: {
          actions: [
            'toggleMultiChoice',
            {type: 'validateStep', params: {step: 'estatePlanning'}},
          ],
        },
        NEXT: {
          target: 'income',
          guard: {type: 'canContinue', params: {step: 'estatePlanning'}},
        },
        BACK: {target: 'insurance'},
      },
    },
    income: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'income'}},
          ],
        },
        NEXT: {
          target: 'investment',
          guard: {type: 'canContinue', params: {step: 'income'}},
        },
        BACK: {target: 'estatePlanning'},
      },
    },
    investment: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'investment'}},
          ],
        },
        NEXT: {
          target: 'realEstate',
          guard: {type: 'canContinue', params: {step: 'investment'}},
        },
        BACK: {target: 'income'},
      },
    },
    realEstate: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'realEstate'}},
          ],
        },
        NEXT: {
          target: 'debt',
          guard: {type: 'canContinue', params: {step: 'realEstate'}},
        },
        BACK: {target: 'investment'},
      },
    },
    debt: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'debt'}},
          ],
        },
        NEXT: {
          target: 'monthlyExpenses',
          guard: {type: 'canContinue', params: {step: 'debt'}},
        },
        BACK: {target: 'realEstate'},
      },
    },
    monthlyExpenses: {
      on: {
        UPDATE_FIELD: {
          actions: [
            'assignField',
            'markTouched',
            {type: 'validateStep', params: {step: 'monthlyExpenses'}},
          ],
        },
        NEXT: {
          target: 'summary',
          guard: {type: 'canContinue', params: {step: 'monthlyExpenses'}},
        },
        BACK: {target: 'debt'},
      },
    },
    summary: {
      on: {
        BACK: {target: 'monthlyExpenses'},
      },
    },
  },
});
